import React, {useEffect, useState} from "react";
import './GIFEncoder'
import './LZWEncoder'
import './NeuQuant'
import {DancelestrationsBackdrop} from './dancelestrations';

const binaryStringToBlob = (str, type) => {
    const bytes = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i) & 0xff;
    return new Blob([bytes], {type});
};

const loadVideo = (url) => new Promise((resolve, reject) => {
    const vid = document.createElement('video');
    vid.autoplay = true;
    vid.muted = true;
    vid.loop = false;
    vid.playsInline = true;
    vid.src = url;

    vid.addEventListener('canplay', () => resolve(vid));
    vid.addEventListener('error', (err) => reject(err));
});

export const CaptureDancingGif = ({pair, guess, existing, updateTask}) => {
    const cardStyle = {
        border: '3px solid black',
        borderRadius: 6,
        backgroundColor: '#ffffff',
        color: 'black',
        boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
    };

    const defaultStatus = null;

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(defaultStatus);
    const [binaryGif, setBinaryGif] = useState(null);
    const [gifBlob, setGifBlob] = useState(null);
    const [width, setWidth] = useState(0);
    const [hasCapturedVideo, setHasCapturedVideo] = useState(false);

    useEffect(() => {
        if(hasCapturedVideo) return;

        document.getElementById('video-upload').onchange = async function() {
            let file = this.files[0];
            if(!file) return;

            const videoObj = await loadVideo(URL.createObjectURL(file));

            if (videoObj.duration >= 6) {
                setStatus("Video must be less than 5 seconds!");
                this.value = null;
                return;
            } else if (videoObj.duration < 1) {
                setStatus("Video must be at least 1 second!");
                this.value = null;
                return;
            } else {
                setStatus("Creating Dance 🕐")
            }

            setHasCapturedVideo(true);

            document.getElementById('inputVideoPreview').appendChild(videoObj);
            let _CANVAS = document.createElement('canvas');
            document.getElementById('hiddenCanvas').appendChild(_CANVAS);

            let displayedHeight= 400;
            let displayedWidth= Math.round(displayedHeight * videoObj.videoWidth / videoObj.videoHeight);

            videoObj['style']['height']=`${displayedHeight}px`;
            videoObj['style']['width']=`${displayedWidth}px`;
            _CANVAS['style']['height']=`${displayedHeight}px`;
            _CANVAS['style']['width']=`${displayedWidth}px`;
            _CANVAS.height=displayedHeight;
            _CANVAS.width=displayedWidth;

            // eslint-disable-next-line no-undef
            const encoder = new GIFEncoder();
            encoder.setRepeat(0);
            encoder.setDelay(250);
            encoder.setQuality(4);

            let continueCallback= true;
            let frameIndex= 0;

            const step = async() => {
                if(frameIndex % 6 === 0) {
                    _CANVAS.getContext('2d', { willReadFrequently: true }).drawImage(videoObj, 0, 0, displayedWidth, displayedHeight);
                    encoder.addFrame(_CANVAS.getContext('2d', { willReadFrequently: true }));
                }

                frameIndex++;

                if(continueCallback) {
                    videoObj.requestVideoFrameCallback(step);
                }
            };

            videoObj.addEventListener('play', () => {
                if(continueCallback) videoObj.requestVideoFrameCallback(step);
                encoder.start();
            }, false);

            videoObj.addEventListener('ended', () => {
                continueCallback=false;
                encoder.finish();
                window.URL.revokeObjectURL(videoObj.src);
                document.getElementById('inputVideoPreview').innerHTML = '';
                setStatus('Done!')

                const readableStream = encoder.stream();
                const blob = binaryStringToBlob(readableStream.getData(), 'image/gif');
                const blobUrl = URL.createObjectURL(blob);

                setWidth(displayedWidth);
                setGifBlob(blob);
                setBinaryGif(blobUrl);
            }, false);

            videoObj.play();
            this.value = null;
        };
    }, [hasCapturedVideo]);

    useEffect(() => {
        if(existing) {
            setStatus('Done!');
            setBinaryGif(existing);
            setWidth(undefined);
            setHasCapturedVideo(true);
        }
    }, [existing]);

    const buttonStyle = {
        ...cardStyle,
        padding: '14px 24px',
        fontSize: 20,
        fontWeight: 700,
        cursor: 'pointer',
        fontFamily: 'inherit',
    };

    const getControls = () => {
        if(existing) return <div style={{
            ...cardStyle,
            padding: '16px 24px',
            textAlign: 'center',
            fontSize: 24,
            fontWeight: 800,
        }}>Done ✓</div>;
        if(loading) return null;
        if(!binaryGif) return null;
        return <div style={{display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap'}}>
            <button style={buttonStyle} onClick={() => {
                if (binaryGif && binaryGif.startsWith('blob:')) URL.revokeObjectURL(binaryGif);
                setStatus(defaultStatus);
                setHasCapturedVideo(false);
                setBinaryGif(null);
                setGifBlob(null);
            }}>❌ Redo</button>
            <button style={buttonStyle} onClick={() => {
                if (!gifBlob) return;
                setLoading(true);
                fetch('/dancelestrations/create-dancing-gif', {
                    method: 'post',
                    credentials: 'include',
                    body: gifBlob
                }).then(() => {
                    updateTask();
                }).catch(() => {
                    setLoading(false);
                })
            }}>✅ Submit</button>
        </div>
    }

    const showStatusCard = typeof status === 'string' && !binaryGif;

    return <div style={{
        alignSelf: 'flex-start',
        minHeight: '100dvh',
        width: '100%',
        boxSizing: 'border-box',
        padding: 'calc(16px + env(safe-area-inset-top)) 16px calc(24px + env(safe-area-inset-bottom))',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 16,
        color: 'white',
    }}>
        <DancelestrationsBackdrop />
        {/* Top: headline */}
        <div style={{
            ...cardStyle,
            alignSelf: 'center',
            textAlign: 'center',
            padding: '10px 24px 14px',
            lineHeight: 1.15,
        }}>
            <div style={{
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: 3,
                textTransform: 'uppercase',
                opacity: 0.6,
                marginBottom: 4,
            }}>Step</div>
            <div style={{fontSize: 32, fontWeight: 700}}>Dance</div>
        </div>

        {/* Middle: instructions / status / preview / GIF */}
        <div style={{display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'stretch'}}>
            {!hasCapturedVideo && !binaryGif && !existing && (
                <div style={{...cardStyle, padding: '14px 16px', textAlign: 'center'}}>
                    <div style={{fontSize: 18, lineHeight: 1.4}}>
                        Take turns. In portrait mode, record a 5 second video of <strong>{pair.name}</strong> dancing your guess:
                    </div>
                    <div style={{
                        fontSize: 26,
                        fontWeight: 700,
                        marginTop: 12,
                        padding: '8px 14px',
                        border: '2px dashed rgba(0,0,0,0.4)',
                        borderRadius: 4,
                        display: 'inline-block',
                    }}>{guess && guess.trim() ? guess : "You Didn't Guess!"}</div>
                </div>
            )}

            <div id='inputVideoPreview' style={{display: 'flex', justifyContent: 'center'}}/>

            {binaryGif && (
                <div style={{display: 'flex', justifyContent: 'center'}}>
                    <img id='outputGif' src={binaryGif} style={{
                        maxWidth: '100%',
                        width: width || 'auto',
                        border: '3px solid black',
                        borderRadius: 6,
                        boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
                        backgroundColor: '#0a0a0a',
                        display: 'block',
                    }}/>
                </div>
            )}
        </div>

        {/* Bottom: record button, status, or redo/submit controls */}
        <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            {showStatusCard && (
                <div style={{...cardStyle, padding: '14px 16px', textAlign: 'center', fontSize: 22, fontWeight: 700}}>
                    {status}
                </div>
            )}

            {!hasCapturedVideo && <>
                <label htmlFor="video-upload" style={{
                    ...cardStyle,
                    padding: '16px 16px',
                    textAlign: 'center',
                    fontSize: 22,
                    fontWeight: 700,
                    cursor: 'pointer',
                }}>🎥 Record Video</label>
                <input id="video-upload" type="file" accept="video/*" capture="environment" style={{display: 'none'}} />
            </>}

            {getControls()}
        </div>

        <div id='hiddenCanvas' style={{display: 'none'}}/>
    </div>;
}